// features/automation/utils/variableRefUtils.js
//
// Pure functions for scanning, analyzing, and remapping {{stepN.xxx}}
// variable references across automation steps.

import { MOCK_DATA_VARIABLES } from '../config/dataSimulatorConstants';
import { stripRefCast } from './placeholderCast';

// A placeholder may carry a ":type" cast — "{{request_data.id:string}}" — that
// tells the engine which JSON type to send. It is not part of the reference, so
// everything in here (validation, remapping, impact analysis) works on the
// reference with the cast stripped off.
const CAST_TAIL = '(:\\s*(?:string|number|boolean|json)\\s*)?';

const VAR_REF_REGEX = /\{\{([^}]+)\}\}/g;
const STEP_REF_REGEX = /^step(\d+)\./;
const STEP_REF_ALIAS_REGEX = /^step(\d+)\.([^.]+)\.(request|response)\./;

// Prefixes that are always valid regardless of step position
// Note: mock.* is validated against VALID_MOCK_REFS below rather than blindly allowed
const ALWAYS_VALID_PREFIXES = [
  'test.', 'test_files.', 'request_data.', 'expected_data.',
  'request.', 'response.', 'permutation.',
];

const VALID_MOCK_REFS = new Set(MOCK_DATA_VARIABLES.map((v) => v.reference));

// ── Scanning ──────────────────────────────────────────────────

/**
 * Extract all {{ref}} inner strings from a single string value.
 */
export const extractAllVarRefs = (value) => {
  if (!value || typeof value !== 'string') return [];
  const refs = [];
  let match;
  const re = new RegExp(VAR_REF_REGEX.source, 'g');
  while ((match = re.exec(value)) !== null) {
    refs.push(stripRefCast(match[1]));
  }
  return refs;
};

/**
 * Parse the step number from a step reference.
 * "step2.1.response.email" → 2
 */
export const parseStepNumber = (ref) => {
  const match = ref.match(STEP_REF_REGEX);
  return match ? parseInt(match[1], 10) : null;
};

/**
 * Filter to only stepN.* references.
 */
export const filterStepRefs = (refs) =>
  refs.filter(({ ref }) => STEP_REF_REGEX.test(ref));

/**
 * Check if a reference is a legacy permutation variable.
 */
export const isPermutationRef = (ref) =>
  typeof ref === 'string' && ref.startsWith('permutation.');

// Helper: scan a single string value and push results
const scanString = (value, location, results) => {
  if (!value || typeof value !== 'string') return;
  for (const ref of extractAllVarRefs(value)) {
    results.push({ ref, location });
  }
};

// Helper: recursively scan a plain JSON object for variable refs
const scanJsonObject = (obj, locationPrefix, results) => {
  if (!obj || typeof obj !== 'object') return;
  if (Array.isArray(obj)) {
    obj.forEach((item, i) => scanJsonObject(item, `${locationPrefix}[${i}]`, results));
    return;
  }
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'string') {
      scanString(value, `${locationPrefix}.${key}`, results);
    } else if (typeof value === 'object' && value !== null) {
      scanJsonObject(value, `${locationPrefix}.${key}`, results);
    }
  }
};

/**
 * Deep-scan a step object for all {{xxx}} variable references.
 * Handles Api, Database, and Web-UI step types.
 * @returns {Array<{ref: string, location: string}>}
 */
export const scanStepForVarRefs = (step) => {
  const results = [];
  if (!step) return results;

  // ── API Step ──
  if (step.stepType === 'Api') {
    // Endpoint
    const url = typeof step.endpoint === 'object' ? step.endpoint?.url : step.endpoint;
    scanString(url, 'endpoint URL', results);

    // Query parameters
    if (step.endpoint?.parameters) {
      for (const [key, val] of Object.entries(step.endpoint.parameters)) {
        scanString(val, `query param "${key}"`, results);
      }
    }

    // Requests
    for (const req of (step.requests || [])) {
      const reqLabel = req.name || 'Request';

      // Headers
      for (const hdr of (req.headers || [])) {
        scanString(hdr.value, `${reqLabel} header "${hdr.name}"`, results);
      }

      // Assertions
      for (const assertion of (req.assertions || [])) {
        scanString(assertion.expectedValue, `${reqLabel} assertion`, results);
      }

      // Body JSON (transient fields — present when loaded from S3)
      if (req._reqBodyJson) {
        scanJsonObject(req._reqBodyJson, `${reqLabel} request body`, results);
      }
      if (req._resBodyJson) {
        scanJsonObject(req._resBodyJson, `${reqLabel} response body`, results);
      }
    }
  }

  // ── Database Step ──
  if (step.stepType === 'Database') {
    const conn = step.connection || {};
    scanString(conn.host, 'DB host', results);
    scanString(conn.databaseName, 'DB name', results);
    scanString(conn.schema, 'DB schema', results);
    scanString(conn.credentials?.username, 'DB username', results);
    scanString(conn.credentials?.password, 'DB password', results);
    scanString(conn.operation?.expression, 'SQL query', results);

    for (const assertion of (step.assertions || [])) {
      scanString(assertion.expectedValue, 'DB assertion', results);
    }
  }

  // ── Web-UI Step ──
  if (step.stepType === 'Web-UI') {
    const stringFields = [
      'locator', 'textToEnter', 'expectedValue', 'additionalValue',
      'searchValue', 'description', 'variableName', 'filterValue',
    ];
    for (const action of (step.uiSteps || [])) {
      const actionLabel = `Action "${action.action || 'unnamed'}"`;
      for (const field of stringFields) {
        scanString(action[field], `${actionLabel} ${field}`, results);
      }
    }
  }

  return results;
};

// ── Order Mapping ─────────────────────────────────────────────

/**
 * Build { oldOrder → newOrder } mapping for a reorder operation.
 */
export const buildReorderMapping = (steps, fromIndex, toIndex) => {
  const simulated = [...steps];
  const [moved] = simulated.splice(fromIndex, 1);
  simulated.splice(toIndex, 0, moved);

  const mapping = {};
  for (let i = 0; i < simulated.length; i++) {
    const oldOrder = simulated[i].order;
    const newOrder = i + 1;
    if (oldOrder !== newOrder) {
      mapping[oldOrder] = newOrder;
    }
  }
  return mapping;
};

/**
 * Build mapping for a delete operation.
 * @returns {{ deletedOrder: number|null, mapping: Object<number,number> }}
 */
export const buildDeleteMapping = (steps, deletedStepId) => {
  const deletedStep = steps.find(s => s.id === deletedStepId);
  if (!deletedStep) return { deletedOrder: null, mapping: {} };

  const deletedOrder = deletedStep.order;
  const remaining = steps.filter(s => s.id !== deletedStepId);

  const mapping = {};
  remaining.forEach((step, i) => {
    const newOrder = i + 1;
    if (step.order !== newOrder) {
      mapping[step.order] = newOrder;
    }
  });

  return { deletedOrder, mapping };
};

// ── String-Level Remapping ────────────────────────────────────

/**
 * Replace {{stepOld.xxx}} with {{stepNew.xxx}} in a string.
 */
export const remapVarRefsInString = (value, orderMapping) => {
  if (!value || typeof value !== 'string' || !value.includes('{{step')) return value;
  return value.replace(/\{\{step(\d+)\./g, (fullMatch, numStr) => {
    const oldOrder = parseInt(numStr, 10);
    const newOrder = orderMapping[oldOrder];
    return newOrder !== undefined ? `{{step${newOrder}.` : fullMatch;
  });
};

/**
 * Blank out refs to deleted step, then remap remaining refs.
 */
export const cleanVarRefsInString = (value, deletedOrder, orderMapping) => {
  if (!value || typeof value !== 'string' || !value.includes('{{step')) return value;

  // Step 1: blank deleted step refs
  let result = value.replace(/\{\{step(\d+)\.[^}]*\}\}/g, (fullMatch, numStr) => {
    return parseInt(numStr, 10) === deletedOrder ? '' : fullMatch;
  });

  // Step 2: remap remaining
  result = remapVarRefsInString(result, orderMapping);
  return result;
};

// ── Step-Level Remapping (immutable) ──────────────────────────

// Helper: apply a string transform to every string in a JSON object tree
const transformJsonObject = (obj, fn) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) {
    let changed = false;
    const next = obj.map(item => {
      const updated = transformJsonObject(item, fn);
      if (updated !== item) changed = true;
      return updated;
    });
    return changed ? next : obj;
  }
  let changed = false;
  const next = {};
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'string') {
      const updated = fn(value);
      next[key] = updated;
      if (updated !== value) changed = true;
    } else if (typeof value === 'object' && value !== null) {
      const updated = transformJsonObject(value, fn);
      next[key] = updated;
      if (updated !== value) changed = true;
    } else {
      next[key] = value;
    }
  }
  return changed ? next : obj;
};

/**
 * Apply remapVarRefsInString to every string field in a step.
 * Returns a new step object if anything changed, or the same object if not.
 */
export const remapVarRefsInStep = (step, orderMapping) => {
  const fn = (val) => remapVarRefsInString(val, orderMapping);
  return transformStepStrings(step, fn);
};

/**
 * Apply cleanVarRefsInString to every string field in a step.
 */
export const cleanVarRefsInStep = (step, deletedOrder, orderMapping) => {
  const fn = (val) => cleanVarRefsInString(val, deletedOrder, orderMapping);
  return transformStepStrings(step, fn);
};

/**
 * Walk all variable-holding string fields in a step and apply a transform function.
 * Returns same step reference if nothing changed (identity check).
 */
const transformStepStrings = (step, fn) => {
  if (!step) return step;
  let changed = false;
  const updates = {};

  // ── API ──
  if (step.stepType === 'Api') {
    // Endpoint
    if (step.endpoint && typeof step.endpoint === 'object') {
      const url = fn(step.endpoint.url);
      let params = step.endpoint.parameters;
      if (params) {
        const updatedParams = {};
        let pChanged = false;
        for (const [k, v] of Object.entries(params)) {
          const nv = fn(v);
          updatedParams[k] = nv;
          if (nv !== v) pChanged = true;
        }
        if (pChanged) params = updatedParams;
      }
      if (url !== step.endpoint.url || params !== step.endpoint.parameters) {
        updates.endpoint = { ...step.endpoint, url, parameters: params };
        changed = true;
      }
    }

    // Requests
    if (step.requests) {
      let reqChanged = false;
      const newReqs = step.requests.map(req => {
        let rChanged = false;
        const newReq = { ...req };

        // Headers
        if (req.headers?.length) {
          const newHeaders = req.headers.map(h => {
            const nv = fn(h.value);
            if (nv !== h.value) { rChanged = true; return { ...h, value: nv }; }
            return h;
          });
          if (rChanged) newReq.headers = newHeaders;
        }

        // Assertions
        if (req.assertions?.length) {
          let aChanged = false;
          const newAssertions = req.assertions.map(a => {
            const nv = fn(a.expectedValue);
            if (nv !== a.expectedValue) { aChanged = true; return { ...a, expectedValue: nv }; }
            return a;
          });
          if (aChanged) { newReq.assertions = newAssertions; rChanged = true; }
        }

        // Body JSON
        if (req._reqBodyJson) {
          const updated = transformJsonObject(req._reqBodyJson, fn);
          if (updated !== req._reqBodyJson) { newReq._reqBodyJson = updated; rChanged = true; }
        }
        if (req._resBodyJson) {
          const updated = transformJsonObject(req._resBodyJson, fn);
          if (updated !== req._resBodyJson) { newReq._resBodyJson = updated; rChanged = true; }
        }

        if (rChanged) reqChanged = true;
        return rChanged ? newReq : req;
      });
      if (reqChanged) { updates.requests = newReqs; changed = true; }
    }
  }

  // ── Database ──
  if (step.stepType === 'Database') {
    if (step.connection) {
      const conn = step.connection;
      const host = fn(conn.host);
      const databaseName = fn(conn.databaseName);
      const schema = fn(conn.schema);
      const username = fn(conn.credentials?.username);
      const password = fn(conn.credentials?.password);
      const expression = fn(conn.operation?.expression);

      if (host !== conn.host || databaseName !== conn.databaseName || schema !== conn.schema ||
          username !== conn.credentials?.username || password !== conn.credentials?.password ||
          expression !== conn.operation?.expression) {
        updates.connection = {
          ...conn,
          host, databaseName, schema,
          credentials: { ...conn.credentials, username, password },
          operation: { ...conn.operation, expression },
        };
        changed = true;
      }
    }

    if (step.assertions?.length) {
      let aChanged = false;
      const newAssertions = step.assertions.map(a => {
        const nv = fn(a.expectedValue);
        if (nv !== a.expectedValue) { aChanged = true; return { ...a, expectedValue: nv }; }
        return a;
      });
      if (aChanged) { updates.assertions = newAssertions; changed = true; }
    }
  }

  // ── Web-UI ──
  if (step.stepType === 'Web-UI') {
    const stringFields = [
      'locator', 'textToEnter', 'expectedValue', 'additionalValue',
      'searchValue', 'description', 'variableName', 'filterValue',
    ];
    if (step.uiSteps?.length) {
      let uChanged = false;
      const newActions = step.uiSteps.map(action => {
        let actionChanged = false;
        const newAction = { ...action };
        for (const field of stringFields) {
          const nv = fn(action[field]);
          if (nv !== action[field]) { newAction[field] = nv; actionChanged = true; }
        }
        if (actionChanged) uChanged = true;
        return actionChanged ? newAction : action;
      });
      if (uChanged) { updates.uiSteps = newActions; changed = true; }
    }
  }

  return changed ? { ...step, ...updates } : step;
};

// ── Extracted-Field Reference Helpers ─────────────────────────

const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Find all references in other steps that point to a specific extracted field
 * (or any of its children).
 * @param {Array} steps — all steps
 * @param {number} sourceStepOrder — the step that owns the field
 * @param {number} reqNum — 1-based request index
 * @param {string} source — "request" or "response"
 * @param {string} fieldPath — dot-separated field path (e.g. "user.address")
 */
export const findExtractedFieldRefImpact = (steps, sourceStepOrder, reqNum, source, fieldPath) => {
  const refPrefix = `step${sourceStepOrder}.${reqNum}.${source}.${fieldPath}`;
  const impacts = [];

  for (const step of steps) {
    if (step.order === sourceStepOrder) continue;
    const allRefs = scanStepForVarRefs(step);
    const affectedRefs = [];

    for (const { ref, location } of allRefs) {
      if (ref === refPrefix || ref.startsWith(refPrefix + '.')) {
        affectedRefs.push({ ref, location });
      }
    }

    if (affectedRefs.length > 0) {
      impacts.push({
        stepId: step.id, stepName: step.name,
        stepOrder: step.order, affectedRefs,
      });
    }
  }
  return impacts;
};

/**
 * Rename an extracted-field path in all {{stepN.reqNum.source.oldPath}} refs.
 * Also handles child paths (oldPath.child → newPath.child).
 */
export const renameFieldPathInString = (value, stepOrder, reqNum, source, oldPath, newPath) => {
  if (!value || typeof value !== 'string') return value;
  const prefix = `step${stepOrder}.${reqNum}.${source}.`;
  const pattern = `\\{\\{${escapeRegex(prefix)}${escapeRegex(oldPath)}(\\.[^}]*)?${CAST_TAIL}\\}\\}`;
  const re = new RegExp(pattern, 'g');
  // Any ":type" cast on the reference is carried over to the new path untouched.
  return value.replace(re, (_, suffix, cast) => `{{${prefix}${newPath}${suffix || ''}${cast || ''}}}`);
};

/**
 * Blank out refs to a specific extracted-field path (and children).
 */
export const removeFieldPathInString = (value, stepOrder, reqNum, source, fieldPath) => {
  if (!value || typeof value !== 'string') return value;
  const prefix = `step${stepOrder}.${reqNum}.${source}.`;
  const pattern = `\\{\\{${escapeRegex(prefix)}${escapeRegex(fieldPath)}(\\.[^}]*)?${CAST_TAIL}\\}\\}`;
  const re = new RegExp(pattern, 'g');
  return value.replace(re, '');
};

/**
 * Apply renameFieldPathInString across every string in a step.
 */
export const renameFieldPathInStep = (step, stepOrder, reqNum, source, oldPath, newPath) => {
  const fn = (val) => renameFieldPathInString(val, stepOrder, reqNum, source, oldPath, newPath);
  return transformStepStrings(step, fn);
};

/**
 * Apply removeFieldPathInString across every string in a step.
 */
export const removeFieldPathInStep = (step, stepOrder, reqNum, source, fieldPath) => {
  const fn = (val) => removeFieldPathInString(val, stepOrder, reqNum, source, fieldPath);
  return transformStepStrings(step, fn);
};

// ── Impact Analysis ───────────────────────────────────────────

/**
 * Analyze the impact of a reorder operation.
 * Returns per-step list of refs that will remap or become invalid (forward-ref).
 */
export const findReorderImpact = (steps, fromIndex, toIndex) => {
  if (fromIndex === toIndex) return [];
  const mapping = buildReorderMapping(steps, fromIndex, toIndex);
  if (Object.keys(mapping).length === 0) return [];

  // Simulate post-reorder positions
  const simulated = [...steps];
  const [moved] = simulated.splice(fromIndex, 1);
  simulated.splice(toIndex, 0, moved);
  const newPositions = {};
  simulated.forEach((s, i) => { newPositions[s.id] = i + 1; });

  const impacts = [];

  for (const step of steps) {
    const refs = filterStepRefs(scanStepForVarRefs(step));
    if (refs.length === 0) continue;

    const newOrderOfThisStep = newPositions[step.id];
    const affectedRefs = [];

    for (const { ref, location } of refs) {
      const referencedOldOrder = parseStepNumber(ref);
      if (referencedOldOrder === null) continue;
      const referencedNewOrder = mapping[referencedOldOrder] ?? referencedOldOrder;

      if (referencedOldOrder !== referencedNewOrder) {
        if (referencedNewOrder >= newOrderOfThisStep) {
          // Forward reference — will be invalid after reorder
          affectedRefs.push({ ref, location, action: 'invalidate' });
        } else {
          affectedRefs.push({
            ref, location, action: 'remap',
            oldRef: `step${referencedOldOrder}`, newRef: `step${referencedNewOrder}`,
          });
        }
      } else if (referencedOldOrder >= newOrderOfThisStep && referencedOldOrder < step.order) {
        // Ref didn't change number but step moved — used to be valid, now forward
        affectedRefs.push({ ref, location, action: 'invalidate' });
      }
    }

    if (affectedRefs.length > 0) {
      impacts.push({
        stepId: step.id, stepName: step.name,
        stepOrder: step.order, affectedRefs,
      });
    }
  }

  return impacts;
};

/**
 * Analyze the impact of a delete operation.
 */
export const findDeleteImpact = (steps, deletedStepId) => {
  const { deletedOrder, mapping } = buildDeleteMapping(steps, deletedStepId);
  if (deletedOrder === null) return [];

  const impacts = [];

  for (const step of steps) {
    if (step.id === deletedStepId) continue;
    const refs = filterStepRefs(scanStepForVarRefs(step));
    if (refs.length === 0) continue;

    const affectedRefs = [];
    for (const { ref, location } of refs) {
      const referencedOrder = parseStepNumber(ref);
      if (referencedOrder === null) continue;

      if (referencedOrder === deletedOrder) {
        affectedRefs.push({ ref, location, action: 'remove' });
      } else if (mapping[referencedOrder] !== undefined) {
        affectedRefs.push({
          ref, location, action: 'remap',
          oldRef: `step${referencedOrder}`, newRef: `step${mapping[referencedOrder]}`,
        });
      }
    }

    if (affectedRefs.length > 0) {
      impacts.push({
        stepId: step.id, stepName: step.name,
        stepOrder: step.order, affectedRefs,
      });
    }
  }

  return impacts;
};

/**
 * Find all invalid variable references across all steps.
 * Used for pre-run validation.
 */
// ── Reuse Step Analysis ──────────────────────────────────────

/**
 * Analyze all variable references in a step being reused from another script.
 * Categorizes each ref by how it should be handled when copied to the current script.
 *
 * @param {Object} step - The source step to analyze
 * @param {Array<string>} currentReqCols - Column names in current script's request data source
 * @param {Array<string>} currentResCols - Column names in current script's response data source
 * @param {Array<{name:string}>} currentTestFiles - Test files in the current script
 * @param {Array<{name:string}>} currentVariables - Test variables in the current script
 * @returns {{ stepRefs, dataRefs, fileRefs, testVarRefs }}
 */
export const analyzeReusedStepVarRefs = (step, currentReqCols = [], currentResCols = [], currentTestFiles = [], currentVariables = [], currentSteps = []) => {
  const allRefs = scanStepForVarRefs(step);

  const stepRefs = [];      // {{stepN.*}} — may match current script's steps
  const dataRefs = [];      // {{request_data.*}} / {{expected_data.*}}
  const fileRefs = [];      // {{test_files.*}}
  const testVarRefs = [];   // {{test.*}}

  const reqColSet = new Set(currentReqCols);
  const resColSet = new Set(currentResCols);
  const fileNameSet = new Set(currentTestFiles.map(f => f.name));
  const varNameSet = new Set(currentVariables.map(v => v.name));

  // Build a lookup of current steps' extracted fields for step ref matching
  // Format: stepOrder → reqIdentifier (numeric index or alias name) → Set of "source.fieldPath"
  const currentExtractedMap = {};
  for (const cs of currentSteps) {
    const reqEntries = {};
    for (let ri = 0; ri < (cs.requests || []).length; ri++) {
      const req = cs.requests[ri];
      const fte = req.fieldsToExtract || {};
      const paths = new Set();
      for (const [, sourceRef] of Object.entries(fte)) {
        paths.add(sourceRef); // e.g. "response.email"
      }
      if (paths.size > 0) {
        reqEntries[ri + 1] = paths; // numeric index
        const alias = req.name || req.alias;
        if (alias) reqEntries[alias] = paths; // alias name
      }
    }
    // Also handle DB steps which have fieldsToExtract at step level
    if (cs.stepType === 'Database' && cs.fieldsToExtract) {
      const paths = new Set();
      for (const [, sourceRef] of Object.entries(cs.fieldsToExtract)) {
        paths.add(sourceRef);
      }
      if (paths.size > 0) reqEntries[1] = paths; // DB uses reqIdx 1 by convention
    }
    if (Object.keys(reqEntries).length > 0) currentExtractedMap[cs.order] = reqEntries;
  }

  // Deduplicate refs by inner string
  const seen = new Set();

  for (const { ref, location } of allRefs) {
    if (seen.has(ref)) continue;
    seen.add(ref);

    // {{stepN.alias.source.fieldPath}} or {{stepN.reqIdx.source.fieldPath}}
    if (STEP_REF_REGEX.test(ref)) {
      const parts = ref.split('.');
      const stepOrder = parseInt(parts[0].replace('step', ''), 10);
      const reqIdentifier = parts[1]; // alias name or numeric index
      const source = parts[2]; // "request" or "response"
      const fieldPath = parts.slice(3).join('.');
      const sourceRef = `${source}.${fieldPath}`;

      // Check if current script has a matching extracted field
      const currentStep = currentSteps.find(cs => cs.order === stepOrder);
      // Try numeric index first, then match by alias/name
      const numericIdx = parseInt(reqIdentifier, 10);
      let extractedPaths;
      if (!isNaN(numericIdx)) {
        extractedPaths = currentExtractedMap[stepOrder]?.[numericIdx];
      } else if (currentStep) {
        // Match by request name/alias
        const reqIdx = (currentStep.requests || []).findIndex(r => (r.name || r.alias) === reqIdentifier);
        if (reqIdx >= 0) extractedPaths = currentExtractedMap[stepOrder]?.[reqIdx + 1];
      }
      const exists = !!(extractedPaths && extractedPaths.has(sourceRef));

      stepRefs.push({
        ref,
        location,
        exists,
        stepOrder,
        reqIdx: isNaN(numericIdx) ? reqIdentifier : numericIdx,
        source,
        fieldPath,
        matchedStepName: exists ? currentStep?.name : null,
      });
      continue;
    }

    // {{request_data.columnName}}
    if (ref.startsWith('request_data.')) {
      const colName = ref.slice('request_data.'.length);
      dataRefs.push({
        ref, location, source: 'request', columnName: colName,
        exists: reqColSet.has(colName),
      });
      continue;
    }

    // {{expected_data.columnName}}
    if (ref.startsWith('expected_data.')) {
      const colName = ref.slice('expected_data.'.length);
      dataRefs.push({
        ref, location, source: 'response', columnName: colName,
        exists: resColSet.has(colName),
      });
      continue;
    }

    // {{test_files.fileName}}
    if (ref.startsWith('test_files.')) {
      const fileName = ref.slice('test_files.'.length);
      fileRefs.push({
        ref, location, fileName,
        exists: fileNameSet.has(fileName),
      });
      continue;
    }

    // {{test.varName}}
    if (ref.startsWith('test.')) {
      const varName = ref.slice('test.'.length);
      testVarRefs.push({
        ref, location, varName,
        exists: varNameSet.has(varName),
      });
      continue;
    }

    // {{mock.*}}, {{request.*}}, {{response.*}}, {{permutation.*}} — kept as-is (local to execution)
  }

  return { stepRefs, dataRefs, fileRefs, testVarRefs };
};

/**
 * Strip specific variable references from a step, replacing them with "".
 * Used to blank out cross-script refs that the user declined to import.
 *
 * @param {Object} step - The step to clean
 * @param {Array<string>} refsToStrip - Array of inner ref strings to blank (e.g. ["step1.1.response.email", "request_data.custom_col"])
 * @returns {Object} New step with refs stripped
 */
export const stripCrossScriptRefs = (step, refsToStrip) => {
  if (!refsToStrip || refsToStrip.length === 0) return step;
  const stripSet = new Set(refsToStrip);
  const fn = (value) => {
    if (!value || typeof value !== 'string' || !value.includes('{{')) return value;
    return value.replace(VAR_REF_REGEX, (fullMatch, inner) =>
      stripSet.has(stripRefCast(inner)) ? '' : fullMatch
    );
  };
  return transformStepStrings(step, fn);
};

/**
 * Analyze variable references for a GROUP of steps being imported.
 * Intra-group step references (step A referencing step B within the group) are
 * marked as valid. External references go through the same validation as single-step import.
 *
 * @param {Array<Object>} groupSteps - The steps in the group (ordered)
 * @param {Array<string>} currentReqCols
 * @param {Array<string>} currentResCols
 * @param {Array<{name:string}>} currentTestFiles
 * @param {Array<{name:string}>} currentVariables
 * @param {Array<Object>} currentSteps - Steps already in the target script
 * @returns {{ stepRefs, dataRefs, fileRefs, testVarRefs, intraGroupRefs }}
 */
export const analyzeReusedGroupVarRefs = (
  groupSteps,
  currentReqCols = [],
  currentResCols = [],
  currentTestFiles = [],
  currentVariables = [],
  currentSteps = []
) => {
  // Build a set of step orders within the group for intra-group detection
  const groupStepOrders = new Set(groupSteps.map(s => s.order));

  // Build extracted fields map for group steps (for intra-group ref matching)
  const groupExtractedMap = {};
  for (const gs of groupSteps) {
    const reqEntries = {};
    for (let ri = 0; ri < (gs.requests || []).length; ri++) {
      const req = gs.requests[ri];
      const fte = req.fieldsToExtract || {};
      const paths = new Set();
      for (const [, sourceRef] of Object.entries(fte)) {
        paths.add(sourceRef);
      }
      if (paths.size > 0) {
        reqEntries[ri + 1] = paths;
        const alias = req.name || req.alias;
        if (alias) reqEntries[alias] = paths;
      }
    }
    if (gs.stepType === 'Database' && gs.fieldsToExtract) {
      const paths = new Set();
      for (const [, sourceRef] of Object.entries(gs.fieldsToExtract)) {
        paths.add(sourceRef);
      }
      if (paths.size > 0) reqEntries[1] = paths;
    }
    if (Object.keys(reqEntries).length > 0) groupExtractedMap[gs.order] = reqEntries;
  }

  // Collect all refs from ALL steps in the group
  const allStepRefs = [];
  const allDataRefs = [];
  const allFileRefs = [];
  const allTestVarRefs = [];
  const intraGroupRefs = [];

  const seen = new Set();

  for (const step of groupSteps) {
    const allRefs = scanStepForVarRefs(step);

    for (const { ref, location } of allRefs) {
      if (seen.has(ref)) continue;
      seen.add(ref);

      // Step references
      if (STEP_REF_REGEX.test(ref)) {
        const parts = ref.split('.');
        const stepOrder = parseInt(parts[0].replace('step', ''), 10);
        const reqIdentifier = parts[1];
        const source = parts[2];
        const fieldPath = parts.slice(3).join('.');
        const sourceRef = `${source}.${fieldPath}`;

        // Check if this is an intra-group reference
        if (groupStepOrders.has(stepOrder)) {
          const numericIdx = parseInt(reqIdentifier, 10);
          let extractedPaths;
          if (!isNaN(numericIdx)) {
            extractedPaths = groupExtractedMap[stepOrder]?.[numericIdx];
          } else {
            const gs = groupSteps.find(s => s.order === stepOrder);
            if (gs) {
              const reqIdx = (gs.requests || []).findIndex(r => (r.name || r.alias) === reqIdentifier);
              if (reqIdx >= 0) extractedPaths = groupExtractedMap[stepOrder]?.[reqIdx + 1];
            }
          }
          const isValid = !!(extractedPaths && extractedPaths.has(sourceRef));
          if (isValid) {
            intraGroupRefs.push({ ref, location, stepOrder, isIntraGroup: true });
            continue; // Skip — this ref is handled within the group
          }
        }

        // External step reference — run against current script's steps
        const analysis = analyzeReusedStepVarRefs(
          step, currentReqCols, currentResCols, currentTestFiles, currentVariables, currentSteps
        );
        const matchedStepRef = analysis.stepRefs.find(sr => sr.ref === ref);
        if (matchedStepRef) {
          allStepRefs.push(matchedStepRef);
        } else {
          allStepRefs.push({
            ref, location, exists: false, stepOrder,
            reqIdx: parseInt(reqIdentifier, 10) || reqIdentifier,
            source, fieldPath, matchedStepName: null,
          });
        }
        continue;
      }

      // Data refs
      if (ref.startsWith('request_data.')) {
        const colName = ref.slice('request_data.'.length);
        allDataRefs.push({
          ref, location, source: 'request', columnName: colName,
          exists: new Set(currentReqCols).has(colName),
        });
        continue;
      }
      if (ref.startsWith('expected_data.')) {
        const colName = ref.slice('expected_data.'.length);
        allDataRefs.push({
          ref, location, source: 'response', columnName: colName,
          exists: new Set(currentResCols).has(colName),
        });
        continue;
      }

      // File refs
      if (ref.startsWith('test_files.')) {
        const fileName = ref.slice('test_files.'.length);
        allFileRefs.push({
          ref, location, fileName,
          exists: new Set(currentTestFiles.map(f => f.name)).has(fileName),
        });
        continue;
      }

      // Test var refs
      if (ref.startsWith('test.')) {
        const varName = ref.slice('test.'.length);
        allTestVarRefs.push({
          ref, location, varName,
          exists: new Set(currentVariables.map(v => v.name)).has(varName),
        });
        continue;
      }
    }
  }

  return {
    stepRefs: allStepRefs,
    dataRefs: allDataRefs,
    fileRefs: allFileRefs,
    testVarRefs: allTestVarRefs,
    intraGroupRefs,
  };
};

const SYSTEM_COLS = new Set([
  'Run', 'Test_Case_Id', 'Description', 'Response_Id', 'Step_Flow',
  'External_Id', 'Platform', 'Project_Key', 'External_Key',
]);

/**
 * @param {Array} steps
 * @param {Object} [context]
 * @param {string[]} [context.requestDataColumns]  - all CSV request columns (incl. custom)
 * @param {string[]} [context.responseDataColumns] - all CSV response/expected columns
 * @param {Array}    [context.testVariables]        - test variable objects (with .name)
 * @param {Array}    [context.testFiles]            - test file objects (with .name)
 */
// Recursively collect all dot-path strings from a field tree
// (mirrors collectFieldPaths in VariableAutocompleteContext)
const collectFieldPaths = (fields, prefix = '') => {
  const paths = new Set();
  if (!fields) return paths;
  for (const field of fields) {
    const path = prefix ? `${prefix}.${field.key}` : field.key;
    paths.add(path);
    if (field.children && field.children.length > 0) {
      for (const child of collectFieldPaths(field.children, path)) paths.add(child);
    }
  }
  return paths;
};

/**
 * @param {Array} steps
 * @param {Object} [context]
 * @param {string[]} [context.requestDataColumns]  - all CSV request columns (incl. custom)
 * @param {string[]} [context.responseDataColumns] - all CSV response/expected columns
 * @param {Array}    [context.testVariables]        - test variable objects (with .name)
 * @param {Array}    [context.testFiles]            - test file objects (with .name)
 */
export const findInvalidVarRefs = (steps, context = {}) => {
  const {
    requestDataColumns = [],
    responseDataColumns = [],
    testVariables = [],
    testFiles = [],
  } = context;

  const reqDataColSet = new Set(requestDataColumns.filter(c => !SYSTEM_COLS.has(c)));
  const resDataColSet = new Set(responseDataColumns.filter(c => !SYSTEM_COLS.has(c)));
  const varNameSet    = new Set(testVariables.map(v => (typeof v === 'string' ? v : v.name)));
  const fileNameSet   = new Set(testFiles.map(f => (typeof f === 'string' ? f : f.name)));

  // Build per-step request/response field-path sets from loaded field trees.
  // _reqFieldTree / _resFieldTree are persisted to the store by useApiStepEditor
  // whenever the user opens the request body editor.
  // If no tree has been loaded yet for a step we skip request./response. validation
  // for that step to avoid false positives.
  const stepReqPaths = {};  // stepId → Set<string>
  const stepResPaths = {};  // stepId → Set<string>
  for (const step of steps) {
    if (step.stepType !== 'Api') continue;
    const reqPaths = new Set();
    const resPaths = new Set();
    for (const req of (step.requests || [])) {
      if (req._reqFieldTree && req._reqFieldTree.length > 0)
        for (const p of collectFieldPaths(req._reqFieldTree)) reqPaths.add(p);
      if (req._resFieldTree && req._resFieldTree.length > 0)
        for (const p of collectFieldPaths(req._resFieldTree)) resPaths.add(p);
    }
    if (reqPaths.size > 0) stepReqPaths[step.id] = reqPaths;
    if (resPaths.size > 0) stepResPaths[step.id] = resPaths;
  }

  const results = [];

  for (const step of steps) {
    const refs = scanStepForVarRefs(step);
    if (refs.length === 0) continue;

    const validStepOrders = new Set(
      steps.filter(s => s.order < step.order).map(s => s.order)
    );

    const invalidRefs = [];
    for (const { ref, location } of refs) {
      // ── Validatable prefixes ──────────────────────────────────

      if (ref.startsWith('request_data.')) {
        const col = ref.slice('request_data.'.length);
        if (!reqDataColSet.has(col))
          invalidRefs.push({ ref, location });
        continue;
      }

      if (ref.startsWith('expected_data.')) {
        const col = ref.slice('expected_data.'.length);
        if (!resDataColSet.has(col))
          invalidRefs.push({ ref, location });
        continue;
      }

      if (ref.startsWith('test.')) {
        const varName = ref.slice('test.'.length);
        if (!varNameSet.has(varName))
          invalidRefs.push({ ref, location });
        continue;
      }

      if (ref.startsWith('test_files.')) {
        const fileName = ref.slice('test_files.'.length);
        if (!fileNameSet.has(fileName))
          invalidRefs.push({ ref, location });
        continue;
      }

      if (ref.startsWith('response.')) {
        const fieldPath = ref.slice('response.'.length);
        const resPaths = stepResPaths[step.id];
        // Only flag if the field tree has been loaded — otherwise skip to avoid false positives
        if (resPaths && !resPaths.has(fieldPath))
          invalidRefs.push({ ref, location });
        continue;
      }

      if (ref.startsWith('request.')) {
        const fieldPath = ref.slice('request.'.length);
        const reqPaths = stepReqPaths[step.id];
        if (reqPaths && !reqPaths.has(fieldPath))
          invalidRefs.push({ ref, location });
        continue;
      }

      // ── Skip-without-checking prefixes ────────────────────────
      // permutation. is generated at runtime — cannot validate statically.
      if (ref.startsWith('permutation.')) continue;

      // mock. refs must match a known mock variable from the simulator constants.
      if (ref.startsWith('mock.')) {
        if (!VALID_MOCK_REFS.has(ref)) invalidRefs.push({ ref, location });
        continue;
      }

      // ── Step references ───────────────────────────────────────
      const stepNum = parseStepNumber(ref);
      if (stepNum === null) {
        if (!ref.includes('.')) continue; // bare-word ui_var — always valid
        invalidRefs.push({ ref, location }); // unrecognised prefix
      } else if (!validStepOrders.has(stepNum)) {
        invalidRefs.push({ ref, location }); // forward ref or non-existent step
      }
    }

    if (invalidRefs.length > 0) {
      results.push({
        stepId: step.id, stepName: step.name,
        stepOrder: step.order, invalidRefs,
      });
    }
  }

  return results;
};
